"use client"
import { useRef, useState, useEffect } from "react"
import * as htmlToImage from "html-to-image"
import { Link } from "react-router-dom"; // Pastikan ini diimpor
import { QRCodeCanvas } from "qrcode.react"
import { jsPDF } from "jspdf"
import { motion, AnimatePresence } from "framer-motion"
// Importing specific icons:
import {
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  CreditCard, // NEW: Icon for Horizontal Layout
UserSquare,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  Square,
  RectangleVertical,
  RectangleHorizontal,
  MonitorPlay,
  Eye,
  Home,
  EyeOff,
} from "lucide-react"

const mbtiColors = {
  defender: "from-blue-700 via-cyan-500 to-blue-300",
  thinker: "from-purple-800 via-pink-500 to-purple-300",
  explorer: "from-yellow-600 via-orange-400 to-yellow-200",
  diplomat: "from-green-800 via-emerald-500 to-green-300",
}

const mbtiBgColors = {
  defender: ["#1e3a8a", "#06b6d4", "#93c5fd"],
  thinker: ["#6b21a8", "#ec4899", "#d8b4fe"],
  explorer: ["#ca8a04", "#fb923c", "#fef08a"],
  diplomat: ["#065f46", "#10b981", "#86efac"],
}

// NEW: Brighter colors for hexagon border based on MBTI type
const mbtiBrightColors = {
    defender: "#06b6d4", // cyan-500
    thinker: "#ec4899", // pink-500
    explorer: "#fb923c", // orange-400
    diplomat: "#10b981", // emerald-500
};


const mbtiPrefixes = {
  defender: "4",
  thinker: "2",
  explorer: "3",
  diplomat: "1",
}

const elementSymbols = {
  defender: "ðŸ›¡ï¸",
  thinker: "ðŸ§ ",
  explorer: "ðŸ§­",
  diplomat: "ðŸŒ³",
}

const availableFonts = [
  { name: "Inter", value: "Inter, sans-serif", url: "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700;800;900&display=swap" },
  { name: "Roboto", value: "Roboto, sans-serif", url: "https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;600;700;800;900&display=swap" },
  { name: "Open Sans", value: "'Open Sans', sans-serif", url: "https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600;700;800;900&display=swap" },
  { name: "Lato", value: "Lato, sans-serif", url: "https://fonts.googleapis.com/css2?family=Lato:wght@300;400;600;700;800;900&display=swap" },
  { name: "Montserrat", value: "Montserrat, sans-serif", url: "https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;600;700;800;900&display=swap" },
  { name: "Poppins", value: "Poppins, sans-serif", url: "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600;700;800;900&display=swap" },
  { name: "Nunito", value: "Nunito, sans-serif", url: "https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;600;700;800;900&display=swap" },
  { name: "Oswald", value: "Oswald, sans-serif", url: "https://fonts.googleapis.com/css2?family=Oswald:wght@300;400;600;700;800;900&display=swap" },
  { name: "Merriweather", value: "Merriweather, serif", url: "https://fonts.googleapis.com/css2?family=Merriweather:wght@300;400;600;700;800;900&display=swap" },
  { name: "Source Sans Pro", value: "'Source Sans Pro', sans-serif", url: "https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@300;400;600;700;800;900&display=swap" },
  { name: "Playfair Display Italic", value: "'Playfair Display', serif", url: "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@1,400;1,700&display=swap" },
  { name: "Lobster Two Italic", value: "'Lobster Two', cursive", url: "https://fonts.googleapis.com/css2?family=Lobster+Two:ital@1&display=swap" },
  { name: "Dancing Script", value: "'Dancing Script', cursive", url: "https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&display=swap" },
  { name: "Lora", value: "Lora, serif", url: "https://fonts.googleapis.com/css2?family=Lora:wght@400;700&display=swap" },
  { name: "PT Sans", value: "'PT Sans', sans-serif", url: "https://fonts.googleapis.com/css2?family=PT+Sans:wght@400;700&display=swap" },
  { name: "Noto Serif", value: "'Noto Serif', serif", url: "https://fonts.googleapis.com/css2?family=Noto+Serif:wght@400;700&display=swap" },
  { name: "Nanum Gothic", value: "'Nanum Gothic', sans-serif", url: "https://fonts.googleapis.com/css2?family=Nanum+Gothic:wght@400;700;800&display=swap" },
  { name: "Kanit", value: "Kanit, sans-serif", url: "https://fonts.googleapis.com/css2?family=Kanit:wght@400;700&display=swap" },
  { name: "Pacifico", value: "Pacifico, cursive", url: "https://fonts.googleapis.com/css2?family=Pacifico&display=swap" },
  { name: "VT323", value: "VT323, monospace", url: "https://fonts.googleapis.com/css2?family=VT323&display=swap" },
];

const suggestedColors = [
  "#FFFFFF", "#000000", "#FF0000", "#00FF00", "#0000FF", "#FFFF00", "#00FFFF", "#FF00FF",
  "#808080", "#C0C0C0", "#800000", "#808000", "#008000", "#800080", "#008080", "#000080"
];

const suggestedGradients = [
  { name: "Morning Sky", colors: ["#89F7FE", "#66A6FF"] },
  { name: "Sunset Warmth", colors: ["#FF8C00", "#FF4500"] },
  { name: "Deep Ocean", colors: ["#00BFFF", "#1E90FF"] },
  { name: "Forest Blend", colors: ["#228B22", "#3CB371"] },
  { name: "Royal Purple", colors: ["#8A2BE2", "#9370DB"] },
  { name: "Autumn Leaves", colors: ["#D2691E", "#FF7F50"] },
  { name: "Cotton Candy", colors: ["#FFD1DC", "#AEC6CF"] },
  { name: "Volcanic Ash", colors: ["#36454F", "#708090"] },
  { name: "Emerald Green", colors: ["#004B49", "#2E8B57"] },
  { name: "Ruby Red", colors: ["#8B0000", "#DC143C"] },
];

const designTemplates = [
  // A Themes
  {
    id: "A-sakura", name: "Sakura Blossom", category: "A", font: "'Dancing Script', cursive",
    useSolidBackground: false, useCustomGradient: true, customGradient: { color1: "#FFDDEE", color2: "#C6E4EE", color3: "#A8C0FF" }, textColor: "#5A189A",
    previewColors: ["#FFDDEE", "#C6E4EE", "#A8C0FF"],
  },
  {
    id: "A-cyberpunk", name: "Cyberpunk Night", category: "A", font: "VT323, monospace",
    useSolidBackground: false, useCustomGradient: true, customGradient: { color1: "#2B0B3F", color2: "#8A2BE2", color3: "#00FFFF" }, textColor: "#EE82EE",
    previewColors: ["#2B0B3F", "#8A2BE2", "#00FFFF"],
  },
  {
    id: "A-ghibli", name: "Ghibli Forest", category: "A", font: "Merriweather, serif",
    useSolidBackground: false, useCustomGradient: true, customGradient: { color1: "#6A5ACD", color2: "#98FB98", color3: "#FFD700" }, textColor: "#2F4F4F",
    previewColors: ["#6A5ACD", "#98FB98", "#FFD700"],
  },
  {
    id: "A-mecha", name: "Mecha Blueprint", category: "A", font: "Oswald, sans-serif",
    useSolidBackground: true, solidBackgroundColor: "#1C2E4A", headerBackgroundColor: "#3E5C76", bodyBackgroundColor: "#0A1128", textColor: "#C0C0C0",
    previewColors: ["#1C2E4A", "#3E5C76", "#0A1128"],
  },
  {
    id: "A-fantasy", name: "Fantasy Realm", category: "A", font: "Lora, serif",
    useSolidBackground: false, useCustomGradient: true, customGradient: { color1: "#4B0082", color2: "#8A2BE2", color3: "#DAA520" }, textColor: "#F0E68C",
    previewColors: ["#4B0082", "#8A2BE2", "#DAA520"],
  },
  {
    id: "A-school", name: "School Days", category: "A", font: "Nunito, sans-serif",
    useSolidBackground: true, solidBackgroundColor: "#E0FFFF", headerBackgroundColor: "#ADD8E6", bodyBackgroundColor: "#F0F8FF", textColor: "#4682B4",
    previewColors: ["#E0FFFF", "#ADD8E6", "#F0F8FF"],
  },
  {
    id: "A-ninja", name: "Ninja Scroll", category: "A", font: "Kanit, sans-serif",
    useSolidBackground: true, solidBackgroundColor: "#36454F", headerBackgroundColor: "#000000", bodyBackgroundColor: "#1E1E1E", textColor: "#FF4500",
    previewColors: ["#36454F", "#000000", "#1E1E1E"],
  },
  {
    id: "A-magicalgirl", name: "Magical Girl", category: "A", font: "Pacifico, cursive",
    useSolidBackground: false, useCustomGradient: true, customGradient: { color1: "#FF69B4", color2: "#FFB6C1", color3: "#E6E6FA" }, textColor: "#8A2BE2",
    previewColors: ["#FF69B4", "#FFB6C1", "#E6E6FA"],
  },
  {
    id: "A-spaceopera", name: "Space Opera", category: "A", font: "Source Sans Pro, sans-serif",
    useSolidBackground: true, solidBackgroundColor: "#000033", headerBackgroundColor: "#330066", bodyBackgroundColor: "#00001A", textColor: "#00FFFF",
    previewColors: ["#000033", "#330066", "#00001A"],
  },
  {
    id: "A-classic", name: "Classic A", category: "A", font: "Playfair Display Italic, serif",
    useSolidBackground: false, useCustomGradient: true, customGradient: { color1: "#C0D9D9", color2: "#87CEEB", color3: "#4682B4" }, textColor: "#1A1A1A",
    previewColors: ["#C0D9D9", "#87CEEB", "#4682B4"],
  },

  // Other Genres
  {
    id: "genre-minimalist", name: "Minimalist Black", category: "General", font: "Inter, sans-serif",
    useSolidBackground: true, solidBackgroundColor: "#1A1A1A", headerBackgroundColor: "#333333", bodyBackgroundColor: "#000000", textColor: "#FFFFFF",
    previewColors: ["#1A1A1A", "#333333", "#000000"],
  },
  {
    id: "genre-vintage", name: "Vintage Sepia", category: "General", font: "Merriweather, serif",
    useSolidBackground: false, useCustomGradient: true, customGradient: { color1: "#704214", color2: "#A0522D", color3: "#D2B48C" }, textColor: "#362000",
    previewColors: ["#704214", "#A0522D", "#D2B48C"],
  },
  {
    id: "genre-sci-fi", name: "Sci-Fi Tech", category: "General", font: "Roboto, sans-serif",
    useSolidBackground: true, solidBackgroundColor: "#0F1626", headerBackgroundColor: "#1B2A41", bodyBackgroundColor: "#090F1A", textColor: "#00FFFF",
    previewColors: ["#0F1626", "#1B2A41", "#090F1A"],
  },
  {
    id: "genre-nature", name: "Nature Green", category: "General", font: "'Open Sans', sans-serif",
    useSolidBackground: false, useCustomGradient: true, customGradient: { color1: "#0F4C5C", color2: "#2A9D8F", color3: "#E9C46A" }, textColor: "#FFFFFF",
    previewColors: ["#0F4C5C", "#2A9D8F", "#E9C46A"],
  },
  {
    id: "genre-classicblue", name: "Classic Blue", category: "General", font: "Lato, sans-serif",
    useSolidBackground: true, solidBackgroundColor: "#003366", headerBackgroundColor: "#0055AA", bodyBackgroundColor: "#002244", textColor: "#F0F8FF",
    previewColors: ["#003366", "#0055AA", "#002244"],
  },
  {
    id: "genre-warmgold", name: "Warm Gold", category: "General", font: "Montserrat, sans-serif",
    useSolidBackground: false, useCustomGradient: true, customGradient: { color1: "#A67C00", color2: "#D4AF37", color3: "#FFD700" }, textColor: "#4B0082",
    previewColors: ["#A67C00", "#D4AF37", "#FFD700"],
  },
  {
    id: "genre-darkmode", name: "Dark Mode Elite", category: "General", font: "Poppins, sans-serif",
    useSolidBackground: true, solidBackgroundColor: "#2C3E50", headerBackgroundColor: "#34495E", bodyBackgroundColor: "#212F3D", textColor: "#ECF0F1",
    previewColors: ["#2C3E50", "#34495E", "#212F3D"],
  },
  {
    id: "genre-pastel", name: "Soft Pastel", category: "General", font: "Nunito, sans-serif",
    useSolidBackground: false, useCustomGradient: true, customGradient: { color1: "#FADADD", color2: "#FFD1DC", color3: "#AEC6CF" }, textColor: "#6A5ACD",
    previewColors: ["#FADADD", "#FFD1DC", "#AEC6CF"],
  },
  {
    id: "genre-hightech", name: "High-Tech Grey", category: "General", font: "'PT Sans', sans-serif",
    useSolidBackground: true, solidBackgroundColor: "#3C4048", headerBackgroundColor: "#5F6368", bodyBackgroundColor: "#2F3238", textColor: "#D0D0D0",
    previewColors: ["#3C4048", "#5F6368", "#2F3238"],
  },
  {
    id: "genre-retro", name: "Retro Pop", category: "General", font: "Oswald, sans-serif",
    useSolidBackground: false, useCustomGradient: true, customGradient: { color1: "#FF4E50", color2: "#FC913A", color3: "#F9D423" }, textColor: "#000000",
    previewColors: ["#FF4E50", "#FC913A", "#F9D423"],
  },
];


// MODIFIED: Updated FORM_STEPS titles and descriptions
const FORM_STEPS = [
  { id: 1, title: "Layout & MBTI", description: "Choose card layout and personality type" },
  { id: 2, title: "Personal Info", description: "Name and profile picture" },
  { id: 3, title: "Typology & Other Details", description: "Additional personality details (Temperament, Zodiac, Alignment)" },
  { id: 4, title: "Motto & Visibility", description: "Personal motto and privacy" },
  { id: 5, title: "Design Mode", description: "Choose a template or customize your card's look" },
  { id: 6, title: "Font & Colors", description: "Advanced font, colors, and border customization" },
  { id: 7, title: "Download", description: "Export your card" },
]

export default function IDCardGenerator() {
  const cardRef = useRef(null)
  const backCardRef = useRef(null)
  const verticalCardRef = useRef(null) // NEW: Ref for the vertical card
  const audioRef = useRef(null)

  const [currentStep, setCurrentStep] = useState(1)

  // NEW: State for layout mode
  const [layoutMode, setLayoutMode] = useState('horizontal'); // 'horizontal' or 'vertical'

  const initialFormState = {
    name: "",
      communityName: "CUSTOM COMMUNITY",
    mbti: "",
    typology1: "XXX",
    typology2: "XXX",
    typology3: "",
    typology4: "",
    typology5: "",
    extraCategory: "XXX",
    profilePic: null,
    motto: "Knowledge is Power, but character is more",
    visibility: "public",
    sloganTipe: "Grow and evolve.",
    sisiGelapTerang: "Shadow: Overthinking | Light: Insightful",
    socialMedia: "IG: @your_handle | TW: @your_handle",
    showBackCard: false,
  }

  const [form, setForm] = useState(initialFormState)

  const [designMode, setDesignMode] = useState("custom");
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const [customFont, setCustomFont] = useState(availableFonts[0].value)
  const [useCustomGradient, setUseCustomGradient] = useState(false)
  const [customGradient, setCustomGradient] = useState({
    color1: "#1e3a8a",
    color2: "#06b6d4",
    color3: "#93c5fd",
  })
  const [customTextColor, setCustomTextColor] = useState("#FFFFFF")

  const [useSolidBackground, setUseSolidBackground] = useState(false)
  const [solidBackgroundColor, setSolidBackgroundColor] = useState("#1e3a8a")
  const [headerBackgroundColor, setHeaderBackgroundColor] = useState("#06b6d4")
  const [bodyBackgroundColor, setBodyBackgroundColor] = useState("#93c5fd")

  // NEW: State for custom hexagon border color
  const [hexagonBorderColor, setHexagonBorderColor] = useState('#FFFFFF');

  const [profilePicScale, setProfilePicScale] = useState(1);
  const [profilePicOffsetX, setProfilePicOffsetX] = useState(0);
  const [profilePicOffsetY, setProfilePicOffsetY] = useState(0);
  const [profilePicRatio, setProfilePicRatio] = useState('default');

  const [zoomPreview, setZoomPreview] = useState(false);

  const [idNumber, setIdNumber] = useState("")
  const [useDefaultPic, setUseDefaultPic] = useState(false)
  const [downloadQuality, setDownloadQuality] = useState("5")
  const [showThankYouPopup, setShowThankYouPopup] = useState(false)

  const mbtiCategory = (mbti) => {
    const type = mbti.toUpperCase()
    if (["ISFJ", "ISTJ", "ESFJ", "ESTJ"].includes(type)) return "defender"
    if (["INTJ", "INTP", "ENTJ", "ENTP"].includes(type)) return "thinker"
    if (["ISTP", "ISFP", "ESTP", "ESFP"].includes(type)) return "explorer"
    if (["INFJ", "INFP", "ENFJ", "ENFP"].includes(type)) return "diplomat"
    return ""
  }

  useEffect(() => {
    const selectedFont = availableFonts.find((font) => font.value === customFont)
    if (selectedFont && selectedFont.url) {
      const link = document.createElement("link")
      link.href = selectedFont.url
      link.rel = "stylesheet"
      link.id = "google-font-link"

      const existingLink = document.getElementById("google-font-link")
      if (existingLink) {
        document.head.removeChild(existingLink)
      }
      document.head.appendChild(link)
    }
  }, [customFont])

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.play().catch(e => console.log("Audio play prevented:", e));

      const pauseAudio = () => {
        if (audio) audio.pause();
      };

      window.addEventListener('beforeunload', pauseAudio);
      window.addEventListener('pagehide', pauseAudio);

      return () => {
        if (audio) audio.pause();
        window.removeEventListener('beforeunload', pauseAudio);
        window.removeEventListener('pagehide', pauseAudio);
      };
    }
  }, []);

  useEffect(() => {
    const prefix = mbtiPrefixes[mbtiCategory(form.mbti)] || "0"
    if (!idNumber || mbtiCategory(form.mbti) !== mbtiCategory(form.mbti)) {
      const randomId = prefix + Math.floor(10000 + Math.random() * 89999).toString()
      setIdNumber(randomId)
    }
  }, [form.mbti, idNumber])

  // NEW: Update hexagon border color when MBTI changes and in custom mode
  useEffect(() => {
      if(layoutMode === 'vertical' && designMode === 'custom') {
        const category = mbtiCategory(form.mbti);
        setHexagonBorderColor(mbtiBrightColors[category] || '#FFFFFF');
      }
  }, [form.mbti, layoutMode, designMode]);


  const handleChange = (e) => {
    const { name, value, files } = e.target
    if (files) {
      if (files[0]) {
        setForm({ ...form, [name]: URL.createObjectURL(files[0]) })
        setUseDefaultPic(false)
        setProfilePicScale(1);
        setProfilePicOffsetX(0);
        setProfilePicOffsetY(0);
      }
    } else {
      setForm({ ...form, [name]: value })
    }
  }

  const handleCustomGradientChange = (colorKey, value) => {
    setCustomGradient((prev) => ({
      ...prev,
      [colorKey]: value,
    }))
  }

  const applyTemplate = (template) => {
    setDesignMode("template");
    setSelectedTemplateId(template.id);
    setCustomFont(template.font);
    setCustomTextColor(template.textColor);
    setUseSolidBackground(template.useSolidBackground);
    setUseCustomGradient(template.useCustomGradient);
    // NEW: When applying template, also set hexagon border for vertical mode consistency
    setHexagonBorderColor(template.textColor);

    if (template.useSolidBackground) {
      setSolidBackgroundColor(template.solidBackgroundColor);
      setHeaderBackgroundColor(template.headerBackgroundColor);
      setBodyBackgroundColor(template.bodyBackgroundColor);
    } else if (template.useCustomGradient) {
      setCustomGradient(template.customGradient);
    } else {
        const currentCategory = mbtiCategory(form.mbti);
        const defaultMbtiColors = mbtiBgColors[currentCategory] || ["#9ca3af", "#e5e7eb", "#f3f4f6"];
        setCustomGradient({
            color1: defaultMbtiColors[0],
            color2: defaultMbtiColors[1],
            color3: defaultMbtiColors[2],
        });
    }
  };

  const resetForm = () => {
    setForm(initialFormState)
    setLayoutMode('horizontal'); // NEW: reset layout mode
    setCurrentStep(1)
    setDesignMode("custom")
    setSelectedTemplateId(null)

    const defaultMbtiColors = mbtiBgColors[mbtiCategory(initialFormState.mbti)] || ["#1e3a8a", "#06b6d4", "#93c5fd"];
    setCustomFont(availableFonts[0].value)
    setUseCustomGradient(false)
    setCustomGradient({
      color1: defaultMbtiColors[0],
      color2: defaultMbtiColors[1],
      color3: defaultMbtiColors[2],
    })
    setCustomTextColor("#FFFFFF")
    setHexagonBorderColor('#FFFFFF'); // NEW: reset hexagon border color
    setUseSolidBackground(false)
    setSolidBackgroundColor(defaultMbtiColors[0])
    setHeaderBackgroundColor(defaultMbtiColors[1])
    setBodyBackgroundColor(defaultMbtiColors[2])
    setIdNumber("")
    setUseDefaultPic(false)
    setDownloadQuality("5")
    setShowThankYouPopup(false)
    setProfilePicScale(1);
    setProfilePicOffsetX(0);
    setProfilePicOffsetY(0);
    setProfilePicRatio('default');
    setZoomPreview(false);
  }

  const isStepValid = (step) => {
    switch (step) {
      case 1:
        return form.mbti !== "" && layoutMode !== ''
      case 2:
        return form.name.trim() !== ""
      case 3:
        return true
      case 4:
        return true
      case 5:
        return true
      case 6:
        return true
      case 7:
        return true
      default:
        return false
    }
  }

  const nextStep = () => {
    if (currentStep < FORM_STEPS.length && isStepValid(currentStep)) {
      setCurrentStep(currentStep + 1)
    }
  }

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const getFontEmbedCSS = () => {
    const selectedFont = availableFonts.find((font) => font.value === customFont)
    if (selectedFont && selectedFont.url) {
      return `@import url('${selectedFont.url}');`
    }
    return ""
  }

  const handleDownloadComplete = async () => {
    setShowThankYouPopup(true)
    if (audioRef.current) {
      audioRef.current.pause()
    }
  }

  // MODIFIED: downloadCard now handles both layouts
  const downloadCard = async (isFront = true) => {
      let elementToDownload, cardWidth, cardHeight;
      const pixelRatio = Number.parseInt(downloadQuality);
      let fileName = `${form.name || "MBTI"}_Card`;

      if (layoutMode === 'vertical') {
          elementToDownload = verticalCardRef.current;
          cardWidth = 320;
          cardHeight = 480; // Vertical dimensions
          fileName += "_Vertical.png";
      } else {
          elementToDownload = isFront ? cardRef.current : backCardRef.current;
          cardWidth = 320;
          cardHeight = 202; // Horizontal dimensions
          fileName += isFront ? "_Front.png" : "_Back.png";
      }

      if (!elementToDownload) return;

      try {
          elementToDownload.scrollIntoView({ behavior: "smooth", block: "center" });
          await new Promise((resolve) => setTimeout(resolve, 300));

          const dataUrl = await htmlToImage.toPng(elementToDownload, {
              pixelRatio: pixelRatio,
              width: cardWidth,
              height: cardHeight,
              fontEmbedCSS: getFontEmbedCSS(),
              backgroundColor: null,
              style: {
                  transform: "scale(1)",
                  transformOrigin: "top left",
                  width: `${cardWidth}px`,
                  height: `${cardHeight}px`,
              },
              filter: (node) => node.tagName !== "SCRIPT",
          });

          const link = document.createElement("a");
          link.download = fileName;
          link.href = dataUrl;
          link.click();

          handleDownloadComplete();
      } catch (error) {
          console.error(`Download card failed:`, error);
          alert(`Download card failed. Please try again.`);
      }
  };

  // MODIFIED: handleDownloadPDF now handles both layouts
  const handleDownloadPDF = async () => {
      let frontElement, frontWidth, frontHeight;

      if(layoutMode === 'vertical') {
        frontElement = verticalCardRef.current;
        frontWidth = 320;
        frontHeight = 480;
      } else {
        frontElement = cardRef.current;
        frontWidth = 320;
        frontHeight = 202;
      }

      if (!frontElement) return;

      try {
          const pixelRatio = 4;
          await new Promise((resolve) => setTimeout(resolve, 300));

          const frontDataUrl = await htmlToImage.toPng(frontElement, {
              pixelRatio,
              width: frontWidth,
              height: frontHeight,
              fontEmbedCSS: getFontEmbedCSS(),
              backgroundColor: null,
              style: {
                  width: `${frontWidth}px`,
                  height: `${frontHeight}px`,
              },
          });

          const pdf = new jsPDF({
              orientation: layoutMode === 'vertical' ? 'portrait' : 'landscape',
              unit: "px",
              format: [frontWidth * 2, frontHeight * 2],
          });

          pdf.addImage(frontDataUrl, "PNG", 0, 0, frontWidth * 2, frontHeight * 2);

          // Back card is only for horizontal mode
          if (layoutMode === 'horizontal' && form.showBackCard && backCardRef.current) {
              const backDataUrl = await htmlToImage.toPng(backCardRef.current, {
                  pixelRatio,
                  width: 320,
                  height: 202,
                  fontEmbedCSS: getFontEmbedCSS(),
                  backgroundColor: null,
                  style: {
                      width: `320px`,
                      height: `202px`,
                  },
              });
              pdf.addPage([320 * 2, 202 * 2], 'landscape');
              pdf.addImage(backDataUrl, "PNG", 0, 0, 320 * 2, 202 * 2);
          }

          pdf.save(`${form.name || "MBTI"}_Card_${layoutMode}.pdf`);
          handleDownloadComplete();
      } catch (error) {
          console.error("PDF download failed:", error);
          alert("PDF download failed. Please try again.");
      }
  };

  const today = new Date().toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })

  const fullTypology = [form.typology1, form.typology2, form.typology3, form.typology4, form.typology5]
    .filter(Boolean)
    .join(" - ")

  const currentCategory = mbtiCategory(form.mbti)
  
  let cardBackgroundStyle = {};
  let currentHeaderBg = "";
  let currentBodyBg = "";
  let currentTextColor = customTextColor;
  let currentFont = customFont;

  if (designMode === "template" && selectedTemplateId) {
    const template = designTemplates.find(t => t.id === selectedTemplateId);
    if (template) {
      currentFont = template.font;
      currentTextColor = template.textColor;
      if (template.useSolidBackground) {
        cardBackgroundStyle = { backgroundColor: template.solidBackgroundColor };
        currentHeaderBg = template.headerBackgroundColor;
        currentBodyBg = template.bodyBackgroundColor;
      } else if (template.useCustomGradient) {
        const { color1, color2, color3 } = template.customGradient;
        cardBackgroundStyle = { background: `linear-gradient(to bottom right, ${color1}, ${color2}, ${color3})` };
        currentHeaderBg = "rgba(255, 255, 255, 0.2)";
        currentBodyBg = "rgba(255, 255, 255, 0.2)";
      } else {
        const defaultMbtiColors = mbtiBgColors[currentCategory] || ["#9ca3af", "#e5e7eb", "#f3f4f6"];
        cardBackgroundStyle = { background: `linear-gradient(to bottom right, ${defaultMbtiColors.join(", ")})` };
        currentHeaderBg = "rgba(255, 255, 255, 0.2)";
        currentBodyBg = "rgba(255, 255, 255, 0.2)";
      }
    }
  } else {
    currentFont = customFont;
    currentTextColor = customTextColor;
    if (useSolidBackground) {
      cardBackgroundStyle = { backgroundColor: solidBackgroundColor };
      currentHeaderBg = headerBackgroundColor;
      currentBodyBg = bodyBackgroundColor;
    } else {
      const defaultMbtiColors = mbtiBgColors[currentCategory] || ["#9ca3af", "#e5e7eb", "#f3f4f6"];
      const actualGradientColors = useCustomGradient
        ? [customGradient.color1, customGradient.color2, customGradient.color3]
        : defaultMbtiColors;
      cardBackgroundStyle = { background: `linear-gradient(to bottom right, ${actualGradientColors.join(", ")})` };
      currentHeaderBg = "rgba(255, 255, 255, 0.2)";
      currentBodyBg = "rgba(255, 255, 255, 0.2)";
    }
  }
  
  const getProfilePicDimensions = () => {
    switch (profilePicRatio) {
      case '1:1': return { width: '80px', height: '80px' };
      case '3:4': return { width: '60px', height: '80px' };
      case '4:3': return { width: '107px', height: '80px' };
      case '16:9': return { width: '142px', height: '80px' };
      default: return { width: '80px', height: '96px' };
    }
  };
  const profilePicContainerStyle = getProfilePicDimensions();

  const symbol = elementSymbols[currentCategory] || "â“"

  const renderProfilePic = () => {
    if (form.profilePic && !useDefaultPic) {
      return (
        <img
          src={form.profilePic || "/placeholder.svg"}
          alt="Profile Photo"
          className="absolute w-full h-full object-cover object-center"
          style={{
            transform: `scale(${profilePicScale}) translate(${profilePicOffsetX}%, ${profilePicOffsetY}%)`,
            transformOrigin: 'center center'
          }}
        />
      )
    }
    return <div className="w-full h-full flex items-center justify-center text-4xl bg-black bg-opacity-10">{symbol}</div>
  }

const renderBackCard = () => {
  const backCardBackgroundStyle = cardBackgroundStyle;
  const backCardHeaderBg = currentHeaderBg;
  const backCardTextColor = currentTextColor;
  const backCardFont = currentFont;

  return (
    <div
      ref={backCardRef}
      className="rounded-lg shadow-xl relative border border-white"
      style={{
        width: "320px",
        height: "202px",
        ...backCardBackgroundStyle,
        fontFamily: backCardFont,
        overflow: "hidden",
        position: "relative",
        zIndex: 5,
        color: backCardTextColor,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '1rem',
        textAlign: 'center'
      }}
    >
      <div
        className="p-2 pb-1 flex items-center justify-between absolute top-0 left-0 right-0 z-10"
        style={{ backgroundColor: backCardHeaderBg }}
      >
        <div className="flex items-center gap-1 pl-1 flex-shrink-0">
          <h2 className="text-sm md:text-base font-bold tracking-widest whitespace-nowrap">TYPOLOGY ID</h2>
        </div>
        <span className="text-[10px] md:text-xs font-mono pr-2 whitespace-nowrap">#{idNumber}</span>
      </div>

      <div className="flex flex-col items-center justify-center space-y-2 mt-4 text-sm flex-grow w-full px-2">
        <p
          className="font-semibold text-base w-full overflow-hidden whitespace-nowrap text-ellipsis"
          style={{ maxWidth: 'calc(100% - 16px)' }}
        >
          {form.sloganTipe || "YOUR TYPE SLOGAN"}
        </p>
        <p
          className="text-xs italic w-full overflow-hidden whitespace-nowrap text-ellipsis"
          style={{ maxWidth: 'calc(100% - 16px)' }}
        >
          {form.sisiGelapTerang || "SHADOW | LIGHT"}
        </p>
        <div className="mt-4 pt-2 border-t border-opacity-30 border-current w-3/4">
          <p
            className="text-xs font-medium uppercase w-full overflow-hidden whitespace-nowrap text-ellipsis"
            style={{ maxWidth: '100%' }}
          >
            {form.socialMedia || "YOUR SOCIAL MEDIA"}
          </p>
        </div>
      </div>
    </div>
  );
};

  // NEW: Function to render the Vertical Card
  const renderVerticalCard = () => {
    const hexagonClipPath = "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)";
    const finalHexagonBorderColor = designMode === 'template'
        ? currentTextColor // In template mode, border color matches text color
        : hexagonBorderColor; // In custom mode, use the state

    return (
      <div
        ref={verticalCardRef}
        className="rounded-lg shadow-xl relative border border-white transition-transform duration-300 ease-out flex flex-col items-center justify-start p-6"
        style={{
          width: "320px",
          height: "480px",
          ...cardBackgroundStyle,
          fontFamily: currentFont,
          overflow: "hidden",
          color: currentTextColor,
          transform: zoomPreview ? 'scale(1.2)' : 'scale(1)',
          transformOrigin: 'center center'
        }}
      >
        <h2 className="text-lg font-bold tracking-widest absolute top-6">TYPOLOGY ID</h2>
<span className="text-xs absolute top-12 opacity-80">{form.communityName || 'CUSTOM COMMUNITY'}</span>
        
        {/* Hexagon Profile Picture with Border */}
        <div className="relative mt-24">
            {/* Outer Hexagon (Border) */}
            <div
                className="transition-colors duration-300"
                style={{
                    width: "180px",
                    height: "207.85px", // height = width * sqrt(3) / 2 * 1.15
                    backgroundColor: finalHexagonBorderColor,
                    clipPath: hexagonClipPath,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {/* Inner Hexagon (Picture) */}
                <div
                    className="relative overflow-hidden"
                    style={{
                        width: "170px",
                        height: "196.30px",
                        clipPath: hexagonClipPath,
                    }}
                >
                    {renderProfilePic()}
                </div>
            </div>
        </div>

        {/* Text Info */}
        <div className="text-center mt-8 space-y-2">
            <p className="font-bold text-3xl drop-shadow-md">{form.name || "Naufal"}</p>
            <p className="font-extrabold text-2xl tracking-widest">{form.mbti || "ESTJ"}</p>
        </div>
        
        {/* ID Number */}
        <div className="absolute bottom-6 text-xs font-mono opacity-70">
            #{idNumber}
        </div>
      </div>
    );
  };


  const inputVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 },
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // MODIFIED: Step 1 now includes Layout Selection
        return (
          <motion.div variants={inputVariants} initial="hidden" animate="visible" transition={{ duration: 0.15 }} className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">1. Choose Card Layout</h3>
                <div className="flex space-x-4">
                    <motion.button
                        onClick={() => setLayoutMode("horizontal")}
                        className={`flex-1 flex flex-col items-center justify-center p-4 rounded-lg text-lg font-semibold transition-colors border-2 ${
                            layoutMode === "horizontal"
                                ? "bg-blue-600 text-white border-blue-700 shadow-md"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300 border-gray-300"
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <CreditCard size={24} className="mb-2"/>
                        Horizontal
                    </motion.button>
                    <motion.button
                        onClick={() => setLayoutMode("vertical")}
                        className={`flex-1 flex flex-col items-center justify-center p-4 rounded-lg text-lg font-semibold transition-colors border-2 ${
                            layoutMode === "vertical"
                                ? "bg-blue-600 text-white border-blue-700 shadow-md"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300 border-gray-300"
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <UserSquare size={24} className="mb-2"/>
                        Vertical
                    </motion.button>
                </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">2. Select Your MBTI Type</h3>
              <motion.select
                name="mbti"
                value={form.mbti}
                onChange={handleChange}
                className="border px-3 py-2 w-full rounded-lg focus:ring-blue-500 focus:border-blue-500 text-lg"
                variants={inputVariants}
              >
                <option value="">Choose your MBTI personality type</option>
                {[
                  "INFJ", "INFP", "INTJ", "INTP", "ENFJ", "ENFP",
                  "ISTJ", "ISFJ", "ESTJ", "ESFJ",
                  "ISTP", "ISFP", "ESTP", "ESFP",
                  "ENTJ", "ENTP",
                ].map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </motion.select>
              {form.mbti && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15 }}
                  className="p-3 bg-green-50 border border-green-200 rounded-lg mt-3"
                >
                  <p className="text-sm text-green-700">
                    Great! You've selected <strong>{form.mbti}</strong> - a {currentCategory} type.
                  </p>
                </motion.div>
              )}
            </div>
          </motion.div>
        )

      case 2:
        return (
          <motion.div variants={inputVariants} initial="hidden" animate="visible" transition={{ duration: 0.15 }} className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Personal Information</h3>
            <motion.div variants={inputVariants}>
              <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
              <input
                name="name"
                placeholder="Enter your full name"
                value={form.name}
                onChange={handleChange}
                className="border px-3 py-2 w-full rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
            </motion.div>
{/* TAMBAHKAN BLOK KODE INI DI SINI */}
<motion.div variants={inputVariants}>
  <label className="block text-sm font-medium text-gray-700 mb-2">Community Name</label>
  <input
    name="communityName"
    placeholder="Enter your community name"
    value={form.communityName}
    onChange={handleChange}
    className="border px-3 py-2 w-full rounded-lg focus:ring-blue-500 focus:border-blue-500"
  />
</motion.div>
            <motion.div variants={inputVariants}>
              <label className="block text-sm font-medium text-gray-700 mb-2">Profile Picture</label>
              <input
                name="profilePic"
                type="file"
                accept="image/*"
                onChange={handleChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />

              <div className="flex items-center space-x-2 mt-3">
                <button
                  type="button"
                  onClick={() => setUseDefaultPic(!useDefaultPic)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    useDefaultPic ? "bg-green-500 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {useDefaultPic ? <Check size={16} className="inline-block mr-2" /> : null}
                  {useDefaultPic ? "Using Default Pic" : "Use Default Pic"}
                </button>
              </div>

              {form.profilePic && !useDefaultPic && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15 }} className="mt-4 p-3 border rounded-lg bg-gray-50">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Adjust Profile Picture</h4>
                  <div className="space-y-3">
                    {/* MODIFIED: Ratio buttons only for horizontal mode, as vertical is fixed hexagon */}
                    {layoutMode === 'horizontal' && (
                      <div>
                        <label htmlFor="picRatio" className="block text-sm font-medium text-gray-700 mb-2">Photo Ratio:</label>
                        <div className="flex gap-2">
                          <motion.button
                            type="button"
                            onClick={() => setProfilePicRatio('default')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors ${profilePicRatio === 'default' ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                          >
                            Default
                          </motion.button>
                          <motion.button
                            type="button"
                            onClick={() => setProfilePicRatio('1:1')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors ${profilePicRatio === '1:1' ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                          >
                            <Square size={16} className="inline-block mr-1" /> 1:1
                          </motion.button>
                          <motion.button
                            type="button"
                            onClick={() => setProfilePicRatio('3:4')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors ${profilePicRatio === '3:4' ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                          >
                            <RectangleVertical size={16} className="inline-block mr-1" /> 3:4
                          </motion.button>
                          <motion.button
                            type="button"
                            onClick={() => setProfilePicRatio('4:3')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors ${profilePicRatio === '4:3' ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                          >
                            <RectangleHorizontal size={16} className="inline-block mr-1" /> 4:3
                          </motion.button>
                          <motion.button
                            type="button"
                            onClick={() => setProfilePicRatio('16:9')}
                            className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors ${profilePicRatio === '16:9' ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                          >
                            <MonitorPlay size={16} className="inline-block mr-1" /> 16:9
                          </motion.button>
                        </div>
                      </div>
                    )}
                    <div>
                      <label htmlFor="picScale" className="block text-xs text-gray-600">Zoom:</label>
                      <input
                        id="picScale"
                        type="range"
                        min="0.5"
                        max="2"
                        step="0.05"
                        value={profilePicScale}
                        onChange={(e) => setProfilePicScale(parseFloat(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-xs text-gray-500">{profilePicScale.toFixed(2)}x</span>
                    </div>
                    <div>
                      <label htmlFor="picOffsetX" className="block text-xs text-gray-600">Horizontal Offset:</label>
                      <input
                        id="picOffsetX"
                        type="range"
                        min="-50"
                        max="50"
                        step="1"
                        value={profilePicOffsetX}
                        onChange={(e) => setProfilePicOffsetX(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-xs text-gray-500">{profilePicOffsetX}%</span>
                    </div>
                    <div>
                      <label htmlFor="picOffsetY" className="block text-xs text-gray-600">Vertical Offset:</label>
                      <input
                        id="picOffsetY"
                        type="range"
                        min="-50"
                        max="50"
                        step="1"
                        value={profilePicOffsetY}
                        onChange={(e) => setProfilePicOffsetY(parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-xs text-gray-500">{profilePicOffsetY}%</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )

      case 3: // This step content is only relevant for horizontal layout
        return (
          <motion.div variants={inputVariants} initial="hidden" animate="visible" transition={{ duration: 0.15 }} className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Typology & Other Details</h3>
            {layoutMode === 'vertical' && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                    This information is only displayed on the Horizontal layout. You can skip this step.
                </div>
            )}
            <div className={`grid grid-cols-2 gap-3 ${layoutMode === 'vertical' ? 'opacity-50' : ''}`}>
              {[1, 2, 3, 4, 5].map((i) => (
                <motion.div key={i} variants={inputVariants}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Typology {i}</label>
                  <input
                    name={`typology${i}`}
                    placeholder={`e.g., "1W9"`}
                    value={form[`typology${i}`]}
                    onChange={handleChange}
                    className="border px-3 py-2 w-full rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    disabled={layoutMode === 'vertical'}
                  />
                </motion.div>
              ))}
            </div>

            <motion.div variants={inputVariants} className={`${layoutMode === 'vertical' ? 'opacity-50' : ''}`}>
              <label className="block text-sm font-medium text-gray-700 mb-2">Temperament / Zodiac / Alignment</label>
              <input
                name="extraCategory"
                placeholder="e.g., Sanguine, Gemini, Lawful Good"
                value={form.extraCategory}
                onChange={handleChange}
                className="border px-3 py-2 w-full rounded-lg focus:ring-blue-500 focus:border-blue-500"
                disabled={layoutMode === 'vertical'}
              />
            </motion.div>
          </motion.div>
        )

      case 4: // This step content is only relevant for horizontal layout
        return (
          <motion.div variants={inputVariants} initial="hidden" animate="visible" transition={{ duration: 0.15 }} className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Motto & Visibility</h3>
            {layoutMode === 'vertical' && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                    This information is only displayed on the Horizontal layout. You can skip this step.
                </div>
            )}
            <motion.div variants={inputVariants} className={`${layoutMode === 'vertical' ? 'opacity-50' : ''}`}>
              <label className="block text-sm font-medium text-gray-700 mb-2">Life Motto</label>
              <textarea
                name="motto"
                placeholder="Enter your personal motto or quote"
                value={form.motto}
                onChange={handleChange}
                rows={3}
                className="border px-3 py-2 w-full rounded-lg focus:ring-blue-500 focus:border-blue-500"
                disabled={layoutMode === 'vertical'}
              />
            </motion.div>

            <motion.div variants={inputVariants} className={`${layoutMode === 'vertical' ? 'opacity-50' : ''}`}>
              <label className="block text-sm font-medium text-gray-700 mb-2">Card Visibility</label>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, visibility: 'public' }))}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center ${
                    form.visibility === "public" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                  disabled={layoutMode === 'vertical'}
                >
                  <Eye size={16} className="mr-2" /> Public
                </button>
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, visibility: 'private' }))}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center ${
                    form.visibility === "private" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                  disabled={layoutMode === 'vertical'}
                >
                  <EyeOff size={16} className="mr-2" /> Private
                </button>
              </div>
            </motion.div>

            {/* Back Card Options */}
            <motion.div variants={inputVariants} className={`p-4 border rounded-lg bg-gray-50 ${layoutMode === 'vertical' ? 'opacity-50' : ''}`}>
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-3">
                <button
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, showBackCard: !prev.showBackCard }))}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    form.showBackCard ? "bg-green-500 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                   disabled={layoutMode === 'vertical'}
                >
                  {form.showBackCard ? <Check size={16} className="inline-block mr-2" /> : null}
                  {form.showBackCard ? "Disable Back Card" : "Enable Back Card"}
                </button>
              </label>
              {form.showBackCard && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15 }} className="space-y-3 mt-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Type Slogan:</label>
                    <input
                      name="sloganTipe"
                      placeholder="Your type slogan or summary"
                      value={form.sloganTipe}
                      onChange={handleChange}
                      className="border px-3 py-2 w-full rounded-lg focus:ring-blue-500 focus:border-blue-500"
                       disabled={layoutMode === 'vertical'}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Shadow / Light Side:</label>
                    <input
                      name="sisiGelapTerang"
                      placeholder="Strengths vs Weaknesses"
                      value={form.sisiGelapTerang}
                      onChange={handleChange}
                      className="border px-3 py-2 w-full rounded-lg focus:ring-blue-500 focus:border-blue-500"
                       disabled={layoutMode === 'vertical'}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Social Media Account:</label>
                    <input
                      name="socialMedia"
                      placeholder="Example: IG: @your_handle"
                      value={form.socialMedia}
                      onChange={handleChange}
                      className="border px-3 py-2 w-full rounded-lg focus:ring-blue-500 focus:border-blue-500"
                      disabled={layoutMode === 'vertical'}
                    />
                  </div>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )

      case 5:
        return (
          <motion.div variants={inputVariants} initial="hidden" animate="visible" transition={{ duration: 0.15 }} className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-800">Choose Your Design Mode</h3>

            <div className="flex space-x-4 mb-6">
              <motion.button
                onClick={() => {
                  setDesignMode("custom");
                  setSelectedTemplateId(null);
                }}
                className={`flex-1 px-4 py-2 rounded-lg text-lg font-semibold transition-colors ${
                  designMode === "custom"
                    ? "bg-blue-600 text-white shadow-md"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Custom Design
              </motion.button>
              <motion.button
                onClick={() => setDesignMode("template")}
                className={`flex-1 px-4 py-2 rounded-lg text-lg font-semibold transition-colors ${
                  designMode === "template"
                    ? "bg-blue-600 text-white shadow-md"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Choose Template
              </motion.button>
            </div>

            {designMode === "template" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15 }} className="space-y-4">
                <p className="text-gray-600">Select a pre-designed template below:</p>
                <h4 className="text-md font-semibold text-gray-700 mt-6 mb-2">A Themes</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {designTemplates.filter(t => t.category === "A").map((template) => (
                    <motion.div
                      key={template.id}
                      className={`relative cursor-pointer rounded-lg overflow-hidden border-2 ${
                        selectedTemplateId === template.id ? "border-blue-600 ring-2 ring-blue-300" : "border-gray-200 hover:border-blue-400"
                      } transition-all duration-200`}
                      onClick={() => applyTemplate(template)}
                    >
                      <div
                        className="w-full h-24 flex items-center justify-center text-xs text-white font-bold"
                        style={{ background: template.useSolidBackground ? template.solidBackgroundColor : `linear-gradient(to bottom right, ${template.previewColors.join(", ")})` }}
                      >
                      </div>
                      <div className="p-2 text-center text-sm font-medium bg-white bg-opacity-90">
                        {template.name}
                      </div>
                      {selectedTemplateId === template.id && (
                        <div className="absolute top-1 right-1 bg-blue-600 text-white rounded-full p-0.5">
                          <Check size={16} />
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
                <h4 className="text-md font-semibold text-gray-700 mt-6 mb-2">General Themes</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {designTemplates.filter(t => t.category === "General").map((template) => (
                    <motion.div
                      key={template.id}
                      className={`relative cursor-pointer rounded-lg overflow-hidden border-2 ${
                        selectedTemplateId === template.id ? "border-blue-600 ring-2 ring-blue-300" : "border-gray-200 hover:border-blue-400"
                      } transition-all duration-200`}
                      onClick={() => applyTemplate(template)}
                    >
                      <div
                        className="w-full h-24 flex items-center justify-center text-xs text-white font-bold"
                        style={{ background: template.useSolidBackground ? template.solidBackgroundColor : `linear-gradient(to bottom right, ${template.previewColors.join(", ")})` }}
                      >
                      </div>
                      <div className="p-2 text-center text-sm font-medium bg-white bg-opacity-90">
                        {template.name}
                      </div>
                      {selectedTemplateId === template.id && (
                        <div className="absolute top-1 right-1 bg-blue-600 text-white rounded-full p-0.5">
                          <Check size={16} />
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </motion.div>
        )

      case 6: // MODIFIED: Added Hexagon Border Color option for vertical layout
        return (
          <motion.div variants={inputVariants} initial="hidden" animate="visible" transition={{ duration: 0.15 }} className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Font & Colors</h3>
            {designMode === "template" && (
              <p className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                You are currently using a template. Custom color and font options are disabled. Switch to "Custom Design" in the previous step to enable them.
              </p>
            )}
            
            {/* NEW: Hexagon Border Color for Vertical Layout in Custom Mode */}
            {layoutMode === 'vertical' && (
                <motion.div variants={inputVariants}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hexagon Border Color</label>
                    <input
                        type="color"
                        value={hexagonBorderColor}
                        onChange={(e) => setHexagonBorderColor(e.target.value)}
                        className="w-full h-10 border rounded-lg cursor-pointer"
                        disabled={designMode === "template"}
                    />
                </motion.div>
            )}

            <motion.div variants={inputVariants}>
              <label className="block text-sm font-medium text-gray-700 mb-2">Font Selection</label>
              <select
                value={customFont}
                onChange={(e) => setCustomFont(e.target.value)}
                className="border px-3 py-2 w-full rounded-lg focus:ring-blue-500 focus:border-blue-500"
                style={{ fontFamily: customFont }}
                disabled={designMode === "template"}
              >
                {availableFonts.map((font) => (
                  <option key={font.name} value={font.value} style={{ fontFamily: font.value }}>
                    {font.name}
                  </option>
                ))}
              </select>
            </motion.div>

            <motion.div variants={inputVariants}>
              <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
              <input
                type="color"
                value={customTextColor}
                onChange={(e) => setCustomTextColor(e.target.value)}
                className="w-full h-10 border rounded-lg cursor-pointer"
                disabled={designMode === "template"}
              />
              <div className="flex flex-wrap gap-2 mt-2">
                {suggestedColors.map((color) => (
                  <motion.div
                    key={color}
                    className="w-6 h-6 rounded cursor-pointer border border-gray-300"
                    style={{ backgroundColor: color }}
                    onClick={() => designMode !== "template" && setCustomTextColor(color)}
                  />
                ))}
              </div>
            </motion.div>
            
            <motion.div variants={inputVariants} className="p-4 border rounded-lg bg-gray-50">
              <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-3">
                <button
                  type="button"
                  onClick={() => {
                    if (designMode !== "template") {
                      setUseSolidBackground(!useSolidBackground);
                      setUseCustomGradient(false);
                    }
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    useSolidBackground && designMode !== "template" ? "bg-green-500 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                  disabled={designMode === "template"}
                >
                  {useSolidBackground && designMode !== "template" ? <Check size={16} className="inline-block mr-2" /> : null}
                  Use Solid Background
                </button>
              </label>
              {useSolidBackground && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3 mt-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Card Background Color</label>
                    <input
                      type="color"
                      value={solidBackgroundColor}
                      onChange={(e) => setSolidBackgroundColor(e.target.value)}
                      className="w-full h-10 border rounded-lg cursor-pointer"
                      disabled={designMode === "template"}
                    />
                  </div>
                   {/* This section is only for horizontal layout */}
                  {layoutMode === 'horizontal' && (
                    <>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Header Background Color</label>
                        <input
                          type="color"
                          value={headerBackgroundColor}
                          onChange={(e) => setHeaderBackgroundColor(e.target.value)}
                          className="w-full h-10 border rounded-lg cursor-pointer"
                          disabled={designMode === "template"}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Body Background Color</label>
                        <input
                          type="color"
                          value={bodyBackgroundColor}
                          onChange={(e) => setBodyBackgroundColor(e.target.value)}
                          className="w-full h-10 border rounded-lg cursor-pointer"
                          disabled={designMode === "template"}
                        />
                      </div>
                    </>
                  )}
                </motion.div>
              )}
            </motion.div>
            
            {!useSolidBackground && (
              <motion.div variants={inputVariants} className="p-4 border rounded-lg bg-gray-50">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-3">
                  <button
                    type="button"
                    onClick={() => {
                      if (designMode !== "template") {
                        setUseCustomGradient(!useCustomGradient);
                      }
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                      useCustomGradient && designMode !== "template" ? "bg-green-500 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                    disabled={designMode === "template"}
                  >
                    {useCustomGradient && designMode !== "template" ? <Check size={16} className="inline-block mr-2" /> : null}
                    Use Custom Gradient
                  </button>
                </label>

                {useCustomGradient && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Start Color</label>
                        <input
                          type="color"
                          value={customGradient.color1}
                          onChange={(e) => handleCustomGradientChange("color1", e.target.value)}
                          className="w-full h-10 border rounded-lg cursor-pointer"
                          disabled={designMode === "template"}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Middle Color</label>
                        <input
                          type="color"
                          value={customGradient.color2}
                          onChange={(e) => handleCustomGradientChange("color2", e.target.value)}
                          className="w-full h-10 border rounded-lg cursor-pointer"
                          disabled={designMode === "template"}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">End Color</label>
                        <input
                          type="color"
                          value={customGradient.color3}
                          onChange={(e) => handleCustomGradientChange("color3", e.target.value)}
                          className="w-full h-10 border rounded-lg cursor-pointer"
                          disabled={designMode === "template"}
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Select Gradient Combination:</h4>
                      <div className="flex flex-wrap gap-2">
                        {suggestedGradients.map((gradient, index) => (
                          <motion.div
                            key={index}
                            className="w-12 h-8 rounded-md cursor-pointer border border-gray-300"
                            style={{ background: `linear-gradient(to bottom right, ${gradient.colors.join(", ")})` }}
                            onClick={() => {
                              if (designMode !== "template") {
                                setUseCustomGradient(true);
                                setCustomGradient({
                                  color1: gradient.colors[0],
                                  color2: gradient.colors[1] || gradient.colors[0],
                                  color3: gradient.colors[2] || gradient.colors[1] || gradient.colors[0],
                                });
                              }
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}
          </motion.div>
        )

      case 7:
        return (
          <motion.div variants={inputVariants} initial="hidden" animate="visible" transition={{ duration: 0.15 }} className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Download Your Card</h3>

            <motion.div variants={inputVariants}>
              <label className="block text-sm font-medium text-gray-700 mb-2">Download Quality</label>
              <select
                value={downloadQuality}
                onChange={(e) => setDownloadQuality(e.target.value)}
                className="border px-3 py-2 w-full rounded-lg focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="3">Social Media (Low - ~960px)</option>
                <option value="5">High Quality (High - ~1600px)</option>
              </select>
            </motion.div>
            
            {/* MODIFIED: Download buttons logic */}
            <div className="flex gap-3 mt-6">
                {layoutMode === 'horizontal' ? (
                    <>
                        <motion.button
                            onClick={() => downloadCard(true)}
                            className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Download PNG (Front)
                        </motion.button>
                        {form.showBackCard && (
                            <motion.button
                                onClick={() => downloadCard(false)}
                                className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                            >
                                Download PNG (Back)
                            </motion.button>
                        )}
                    </>
                ) : (
                    <motion.button
                        onClick={() => downloadCard()}
                        className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Download PNG
                    </motion.button>
                )}
              <motion.button
                onClick={handleDownloadPDF}
                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Download PDF
              </motion.button>
            </div>

            <div className="mt-4 flex gap-2">
              <h4 className="text-sm font-medium text-gray-700">Preview:</h4>
              <motion.button
                onClick={() => setZoomPreview(!zoomPreview)}
                className="flex items-center space-x-2 px-3 py-1 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-xs"
              >
                {zoomPreview ? <ZoomOut size={16} /> : <ZoomIn size={16} />}
                <span>{zoomPreview ? 'Zoom Out' : 'Zoom In'}</span>
              </motion.button>
            </div>


            <motion.div variants={inputVariants} className="mt-6 p-4 border border-blue-200 bg-blue-50 rounded-lg text-sm text-blue-800">
              <h4 className="font-semibold mb-2">Download Stuck? Just Screenshot! ðŸ˜‰</h4>
              <p>
                Is the download button misbehaving? Or maybe the dev is just lazy to fix bugs? No worries! <strong className="text-red-600">Just screenshot it!</strong>
                But, to make it look good, <strong className="text-red-600">press "Zoom In" above first!</strong>
              </p>
            </motion.div>
          </motion.div>
        )

      default:
        return null
    }
  }

  const progress = (currentStep / FORM_STEPS.length) * 100

  const fixedWebBackgroundStyle = {
    background: `linear-gradient(to bottom, #87CEEB, #E0FFFF, #ADD8E6)`,
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={"main-container"}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={fixedWebBackgroundStyle}
        className="min-h-screen p-6 transition-colors duration-1000 font-sans"
      >
        <audio ref={audioRef} autoPlay loop preload="auto" style={{ display: "none" }}>
          <source src="/music/sound.mp3" type="audio/mpeg" />
        </audio>

        <nav className="flex items-center justify-between text-white mb-6 font-sans">
                    <Link to="/" className="inline-flex items-center gap-2 px-4 py-2 bg-black/20 border border-white/10 rounded-full text-sm font-medium text-gray-300 hover:bg-white/10 hover:text-white transition-colors">
            <Home size={16} />
            <span>Kembali ke Home</span>
          </Link>
          <div className="text-sm opacity-80">
            Step {currentStep} of {FORM_STEPS.length}
          </div>
        </nav>

        <div className="bg-opacity-90 p-6 rounded-xl max-w-6xl mx-auto shadow-lg border border-white font-sans bg-[rgba(220,238,255,0.6019316493313521)]">
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>

              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-800">{FORM_STEPS[currentStep - 1]?.title}</h2>
                <p className="text-gray-600 text-sm">{FORM_STEPS[currentStep - 1]?.description}</p>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.15 }}
                >
                  {renderStepContent()}
                </motion.div>
              </AnimatePresence>

              <div className="flex justify-between pt-6 border-t">
                <motion.button
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                    currentStep === 1
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-gray-600 text-white hover:bg-gray-700"
                  }`}
                >
                  <ChevronLeft size={16} />
                  <span>Previous</span>
                </motion.button>

                <motion.button
                  onClick={nextStep}
                  disabled={currentStep === FORM_STEPS.length || !isStepValid(currentStep)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                    currentStep === FORM_STEPS.length || !isStepValid(currentStep)
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  <span>Next</span>
                  <ChevronRight size={16} />
                </motion.button>
              </div>

              <div className="flex justify-center pt-4">
                <motion.button
                  onClick={resetForm}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  <RotateCcw size={16} />
                  <span>Start Over</span>
                </motion.button>
              </div>
            </div>
            
            {/* MODIFIED: Card Preview Section with conditional rendering */}
            <div className="space-y-6 flex flex-col items-center">
              <h3 className="text-lg font-semibold text-gray-800">Live Preview</h3>

              <div className="flex flex-col items-center space-y-4" style={{ minHeight: "500px" }}>
                  {layoutMode === 'vertical' ? (
                      renderVerticalCard()
                  ) : (
                    <>
                      {/* Render Horizontal Card */}
                      <div
                        ref={cardRef}
                        className="rounded-lg shadow-xl relative border border-white transition-transform duration-300 ease-out"
                        style={{
                          width: "320px",
                          height: "202px",
                          ...cardBackgroundStyle,
                          fontFamily: currentFont,
                          overflow: "hidden",
                          position: "relative",
                          zIndex: zoomPreview ? 10 : 1,
                          color: currentTextColor,
                          transform: zoomPreview ? 'scale(1.5)' : 'scale(1)',
                          transformOrigin: 'center center'
                        }}
                      >
                         <div
                          className="p-2 pb-1 flex items-center justify-between relative z-10"
                          style={{ backgroundColor: currentHeaderBg }}
                        >
                          <div className="flex items-center gap-1 pl-1 flex-shrink-0">
                            <h2 className="text-sm md:text-base font-bold tracking-widest whitespace-nowrap">TYPOLOGY ID</h2>
                          </div>
                          <span className="text-[10px] md:text-xs font-mono pr-2 whitespace-nowrap">#{idNumber}</span>
                        </div>
                        <div className="absolute top-12 right-2 text-[10px] px-2 font-bold text-yellow-300 py-0.5 z-20">
                          {form.visibility === "public" ? "PUBLIC" : "PRIVATE"}
                        </div>
                        <div className="flex px-3 py-2 gap-3 mt-1">
                          <div className="flex flex-col items-center flex-shrink-0">
                            <div
                              className="rounded overflow-hidden border-2 border-white border-opacity-30 relative"
                              style={{
                                backgroundColor: currentBodyBg,
                                ...profilePicContainerStyle
                              }}
                            >
                              {renderProfilePic()}
                            </div>
                            <div className="text-[7px] italic mt-0.5" style={{ color: currentTextColor }}>{today}</div>
                          </div>
                          <div className="text-xs space-y-0.5 leading-tight flex-grow min-w-0" style={{ fontFamily: currentFont, color: currentTextColor }}>
                            <p className="font-semibold text-sm leading-tight mt-1 truncate">{form.name || "YOUR NAME"}</p>
                            <p className="uppercase text-base font-extrabold tracking-wider drop-shadow-sm">
                              {form.mbti || "MBTI"}
                            </p>
                            <p className="text-[10px] leading-snug truncate">{fullTypology || "TYPOLOGY-TYPOLOGY"}</p>
                            <p className="text-[10px] leading-snug truncate">{form.extraCategory || "TEMPERAMENT/ZODIAC/ALIGNMENT"}</p>
                            {form.motto && (
                              <i className="text-[9px] leading-snug whitespace-pre-wrap break-words max-w-[170px] block">
                                "{form.motto}"
                              </i>
                            )}
                          </div>
                        </div>
                        <div className="absolute bottom-4 left-3 text-[7px] italic whitespace-nowrap" style={{ color: currentTextColor }}>
                          "Unlocking the soul through structure and typology."
                        </div>
                        <div className="absolute bottom-2 left-3 text-[5px] italic whitespace-nowrap" style={{ color: currentTextColor }}>
                          Expired:{" "}
                          {new Date(new Date().setFullYear(new Date().getFullYear() + 50)).toISOString().split("T")[0]}
                        </div>
                        <div className="absolute bottom-2 right-[56px] text-[6px] italic" style={{ color: currentTextColor }}>#{idNumber}</div>
                        <div className="absolute bottom-1 right-1">
                          <QRCodeCanvas
                            value={`${form.name} - ${idNumber}`}
                            size={45}
                            fgColor={currentTextColor}
                            bgColor="transparent"
                          />
                        </div>
                      </div>

                      {form.showBackCard && (
                        <div className="mt-4">
                          <h4 className="text-lg font-semibold text-gray-800">Back Card Preview</h4>
                          {renderBackCard()}
                        </div>
                      )}
                    </>
                  )}
              </div>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {showThankYouPopup && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50"
            >
              <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full relative text-center">
                <button
                  onClick={() => setShowThankYouPopup(false)}
                  className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
                <h2 className="text-3xl font-bold text-blue-600 mb-4">Thank You!</h2>
                <p className="text-gray-700 mb-6 text-lg">
                  Your MBTI ID Card has been successfully generated.
                </p>
                <p className="text-gray-600 mb-2">
                  Created by <span className="font-semibold text-purple-700">Nawfal, ENTJ 1w9</span>
                </p>
                <p className="text-sm text-gray-500">
                  Find me on Facebook: Nawfal sayf al-aswad
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  )
}
